import { Link } from "react-router-dom";
import CTASection from "@/components/CTASection";
import { BRAND } from "@/lib/content";
import useSEO from "@/lib/useSEO";

const PRINCIPLES = [
  { t: "Plain English.", b: "No legalese in your inbox. The legal precision belongs in the engagement letter." },
  { t: "Speed matters.", b: "A demonetization doesn't wait for office hours. We aim to reply to every request within one business day." },
  { t: "Price before work.", b: "Flat fees on defined work. Hourly only where it has to be, with a written scope first." },
  { t: "Confidential by default.", b: "Requests are used only to review and match. Privilege attaches once you engage an attorney." },
  { t: "No fake guarantees.", b: "No promised outcomes. You get the realistic range, and what each path costs." },
  { t: "Here for the next one.", b: "The point is to be the first call for every problem your channel has, not just this one." },
];

export default function About() {
  useSEO({
    title: "About | Upload Counsel",
    description: "Upload Counsel is a legal concierge and referral service for creators, operated by a licensed attorney. Not a law firm.",
    canonical: "https://uploadlawyer.com/about",
  });
  return (
    <div data-testid="page-about">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 pt-16 pb-14 md:pt-24">
          <p className="eyebrow">About</p>
          <h1 className="mt-5 font-display text-5xl sm:text-6xl text-[#F4F1EA] max-w-3xl leading-[1.02]">
            A legal desk built for the way creators work.
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Creators, streamers, podcasters, newsletter writers and the businesses around them run into legal problems traditional firms
            aren't set up for: platform policy, sponsorship terms, audience-facing products, and a pace measured in hours. Upload Counsel
            is the one place to bring all of it.
          </p>
        </div>
      </section>

      <section className="relative py-16 border-t border-[#272B36]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-[#272B36] bg-[#121419] p-8">
            <p className="eyebrow">The model</p>
            <h2 className="mt-3 font-display text-3xl text-[#F4F1EA]">A concierge, not a law firm.</h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Upload Counsel is a legal concierge and referral service operated by a licensed attorney. We review each request, work out
              what kind of problem it is and where it has to be handled, and connect you with an attorney in our network who does that work.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              For larger matters that fit, we may refer you to the founder's affiliated firm. Whoever you engage, you engage them directly
              under their own written engagement letter.
            </p>
          </div>
          <div className="rounded-3xl border border-[#272B36] bg-[#121419] p-8">
            <p className="eyebrow">The network</p>
            <h2 className="mt-3 font-display text-3xl text-[#F4F1EA]">Vetted, then matched.</h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Network attorneys are checked with their state bar, cover specific practice areas, and commit to a written scope and price before
              any work. You aren't handed a list to call around; you're introduced to one attorney chosen for your matter.
            </p>
            <Link to="/attorneys" className="mt-5 inline-block text-[#D9B36C] underline underline-offset-4">Attorneys: apply to join</Link>
          </div>
        </div>
      </section>

      <section className="relative py-16 border-t border-[#272B36]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <p className="eyebrow">Principles</p>
          <h2 className="mt-3 font-display text-4xl text-[#F4F1EA]">How we operate.</h2>
          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {PRINCIPLES.map((p) => (
              <div key={p.t} className="rounded-2xl border border-[#272B36] bg-[#121419] p-6">
                <p className="font-semibold text-[#F4F1EA]">{p.t}</p>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-16 border-t border-[#272B36]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl border border-[#272B36] bg-[#0F1116] p-8 md:p-12">
            <p className="eyebrow">Licensing</p>
            <h2 className="mt-3 font-display text-3xl text-[#F4F1EA]">Where we stand.</h2>
            <dl className="mt-6 grid sm:grid-cols-2 gap-6 text-sm">
              <div>
                <dt className="text-slate-500 uppercase tracking-widest text-xs">Operating attorney</dt>
                <dd className="mt-1 text-[#F4F1EA]">{BRAND.responsible_attorney || "Licensed attorney"}, State Bar of {BRAND.bar_state}</dd>
                <dt className="text-slate-500 uppercase tracking-widest text-xs mt-5">Based in</dt>
                <dd className="mt-1 text-slate-300">{BRAND.jurisdiction}, United States</dd>
              </div>
              <div>
                <dt className="text-slate-500 uppercase tracking-widest text-xs">Network</dt>
                <dd className="mt-1 text-slate-300">Attorneys licensed in their own states, matched by jurisdiction</dd>
                <dt className="text-slate-500 uppercase tracking-widest text-xs mt-5">Practice areas</dt>
                <dd className="mt-1 text-slate-300">Platform disputes, creator contracts, IP and trademark, business setup, reputation, monetization</dd>
              </div>
            </dl>
            <p className="mt-8 text-xs text-slate-500 leading-relaxed">
              Attorney advertising. Prior results do not guarantee a similar outcome. Using this site does not create an attorney-client
              relationship. Legal services are provided by independently engaged attorneys under their own engagement letters. See the full{" "}
              <Link to="/legal/disclaimer" className="underline text-[#D9B36C]">disclaimer</Link>.
            </p>
          </div>
        </div>
      </section>

      <CTASection title="Ready to talk?" body="Send a request. A licensed attorney will read it, and we aim to reply within one business day." />
    </div>
  );
}
